import { useState, useEffect } from 'react';
import { useParams, Link, useLocation, Navigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FullscreenError from '@/components/utils/FullscreenError';
import SwingTradeAdjustementsTable from '@/components/trading/SwingTradeAdjustementsTable';
import SymbolPriceChart from '@/components/ui/SymbolPriceChart';
import FavoriteControl from '@/components/trading/FavoriteControl';
import ShareTradeImageButton from '@/components/trading/ShareTradeImageButton';
import {
  faSpinner,
  faBullseye,
  faArrowLeft,
  faCalendarAlt,
  faChartLine,
  faComments
} from '@/fontawesome';
import { handleAPIError } from '@/utils/toasts';
import { formatDate, formatCurrency, formatCurrencyCentValue, calculatePremiumPercentage, formatRFCDate, rfcDateToTimestamp, formatPercentage } from '@/utils/helpers';
import { getStateLabel, getStateBadgeColor, getTradingRecommendationTypeFromPath, getListLinkForType, isFinishedTrade } from '@/utils/trading-recommendation-helpers';
import { APIRoutes } from '@/services/api.routes';
import { PAGINATION } from '@/config';
import type {
  RecommendationCore,
  RecommendationEarnings,
  RecommendationSwingtrade,
  LongTermRecommendation,
  CoreRecommendationID,
  EarningsRecommendationID,
  SwingTradeID,
  LongTermRecommendationID,
  FeatureConfigKey,
  RecommendationFavorite
} from '@/models';
import type { GraphPoint } from '@/modelsAPI';
import { useFeatureAccess } from '@/hooks/useFeatureAccess';

// Union type for all recommendation types
type AnyRecommendation = RecommendationCore | RecommendationEarnings | RecommendationSwingtrade | LongTermRecommendation;
import type { TradingRecommendationType } from '@/modelsLocal/trading-recommendation';

// Feature mapping for access control
const FEATURE_MAP: Record<TradingRecommendationType, FeatureConfigKey> = {
  CORE: 'coreRecommendations',
  EARNINGS: 'earningRecommendations',
  SWING: 'swingTradeRecommendations',
  LONGTERM: 'longTermRecommendations',
};

export default function TradingRecommendationDetail() {
  const { recommendationId } = useParams<{ recommendationId: string }>();
  const location = useLocation();
  const [recommendation, setRecommendation] = useState<AnyRecommendation | null>(null);
  const [recommendationType, setRecommendationType] = useState<TradingRecommendationType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [criticalError, setCriticalError] = useState<boolean>(false);
  const [accessDenied, setAccessDenied] = useState<boolean>(false);
  const [graphPoints, setGraphPoints] = useState<GraphPoint[]>([]);
  const [graphLoading, setGraphLoading] = useState(false);
  const { hasFeature } = useFeatureAccess();

  useEffect(() => {
    const type = getTradingRecommendationTypeFromPath(location.pathname);
    setRecommendationType(type || 'CORE'); // Fallback to CORE if null
  }, [location.pathname]);

  useEffect(() => {
    if (recommendationId && recommendationType) {
      fetchRecommendationDetails();
    }
  }, [recommendationId, recommendationType]);

  const fetchRecommendationDetails = async () => {
    if (!recommendationId || !recommendationType) return;

    try {
      setLoading(true);
      setError(null);
      setCriticalError(false);
      setAccessDenied(false);

      let response: AnyRecommendation;

      switch (recommendationType) {
        case 'CORE':
          response = await APIRoutes.getCoreRecommendation(recommendationId as CoreRecommendationID);
          break;
        case 'EARNINGS':
          response = await APIRoutes.getEarningsRecommendation(recommendationId as EarningsRecommendationID);
          break;
        case 'SWING':
          response = await APIRoutes.getSwingTrade(recommendationId as SwingTradeID);
          break;
        case 'LONGTERM':
          response = await APIRoutes.getLongTermTrade(recommendationId as LongTermRecommendationID);
          break;
        default:
          // This should never happen due to type checking
          setError('Unbekannter Empfehlungstyp');
          setCriticalError(true);
          return;
      }

      // Validate that we received a valid recommendation
      if (!response || !response.id) {
        setError('Empfehlung nicht gefunden');
        setCriticalError(true);
        return;
      }

      // Security Note: Route-level ProtectedRoute component ensures users have
      // the required feature permissions before accessing this page. The backend
      // API also enforces authorization. If the API call succeeds, the user has
      // proper permissions for this recommendation type.

      setRecommendation(response);

      // Fetch symbol graph data if symbol exists
      if (response.symbol?.id) {
        await fetchSymbolGraph(response);
      }
    } catch (err) {
      if (err instanceof Error && err.message === 'access denied to this recommendation') {
        setAccessDenied(true);
      } else {
        console.error('Critical error loading trading recommendation details:', err);
        setError('Backend nicht erreichbar oder Fehler beim Laden der Trading-Empfehlung');
        setCriticalError(true);
        handleAPIError(err, 'Die Trading-Empfehlung konnte nicht geladen werden. Bitte lade die Seite neu oder versuche es später erneut.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFavoriteChange = (favorite: RecommendationFavorite | null) => {
    setRecommendation(prev => (prev !== null ? { ...prev, favorite } : prev));
  };

  const fetchSymbolGraph = async (rec: AnyRecommendation) => {
    if (!rec.symbol?.id) return;

    try {
      setGraphLoading(true);

      // Determine timestamp range based on recommendation type
      // - CoreRecommendation/EarningsRecommendation: use startDate/endDate (RFCDate format)
      // - SwingTrade/LongTermRecommendation: use startTimestamp/endTimestamp (RFC3339 format)
      let start: string;
      let end: string;

      if ('startDate' in rec) {
        // CoreRecommendation or EarningsRecommendation
        start = rfcDateToTimestamp(rec.startDate, false);
        end = rec.endDate
          ? rfcDateToTimestamp(rec.endDate, true)
          : new Date().toISOString();
      } else if ('startTimestamp' in rec) {
        // SwingTrade or LongTermRecommendation
        start = rec.startTimestamp;
        end = rec.endTimestamp ?? new Date().toISOString();
      } else {
        // Fallback for unknown types
        start = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
        end = new Date().toISOString();
      }

      const response = await APIRoutes.getSymbolGraph(rec.symbol.id, {
        start,
        end,
        points: PAGINATION.CHART.DETAIL_POINTS
      });

      setGraphPoints(response.points);
    } catch (err) {
      console.error('Error loading symbol graph:', err);
      // Don't show critical error for graph, just log it
    } finally {
      setGraphLoading(false);
    }
  };


  // Redirect to feature-locked page if access was denied by backend
  if (accessDenied && recommendationType !== null) {
    const requiredFeature = FEATURE_MAP[recommendationType];
    return <Navigate to={`/feature-locked/${requiredFeature}`} replace />;
  }

  // Show fullscreen error if critical error occurred
  if (criticalError) {
    return (
      <FullscreenError
        title="Trading-Empfehlung nicht verfügbar"
        message={error || 'Die Trading-Empfehlung konnte nicht geladen werden. Möglicherweise ist das Backend nicht erreichbar.'}
        onRetry={fetchRecommendationDetails}
      />
    );
  }

  if (loading) {
    return (
      <>
        <div className="flex justify-center items-center py-12">
          <FontAwesomeIcon icon={faSpinner} className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin" />
        </div>
      </>
    );
  }

  // Feature access check: redirect to feature-locked page if user doesn't have access and trade is not free
  if (recommendation !== null && !recommendation.free && recommendationType !== null) {
    const requiredFeature = FEATURE_MAP[recommendationType];
    if (!hasFeature(requiredFeature)) {
      return <Navigate to={`/feature-locked/${requiredFeature}`} replace />;
    }
  }

  if (!recommendation) {
    return (
      <>
        <div className="text-center py-12">
            <FontAwesomeIcon icon={faBullseye} className="w-16 h-16 text-gray-400 dark:text-gray-600 mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 dark:text-gray-400 mb-2">
              Trading-Empfehlung nicht gefunden
            </h3>
            <p className="text-gray-500 dark:text-gray-500 mb-6">
              Die angeforderte Trading-Empfehlung existiert nicht oder wurde entfernt.
            </p>
            <Link
              to={getListLinkForType(recommendationType)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors cursor-pointer"
            >
              Zurück zur Liste
            </Link>
        </div>
      </>
    );
  }

  const isFinishedSwingTrade = recommendationType === 'SWING'
    && isFinishedTrade(recommendation as RecommendationSwingtrade);

  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <Link
              to={getListLinkForType(recommendationType)}
              className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors cursor-pointer"
            >
              <FontAwesomeIcon icon={faArrowLeft} className="w-5 h-5 mr-2" />
              Zurück zur Liste
            </Link>
          </div>
          <div className="flex items-center gap-2">
            {/* Nur für abgeschlossene Swing-/Langzeit-Trades, also die History-Werte */}
            {(recommendationType === 'SWING' || recommendationType === 'LONGTERM')
              && isFinishedTrade(recommendation as RecommendationSwingtrade | LongTermRecommendation) && (
              <ShareTradeImageButton
                trade={recommendation as RecommendationSwingtrade | LongTermRecommendation}
                type={recommendationType}
              />
            )}
            {recommendationType !== null && (
              <FavoriteControl
                type={recommendationType}
                recommendationId={recommendation.id}
                favorite={recommendation.favorite}
                onChange={handleFavoriteChange}
              />
            )}
          </div>
        </div>

        <div className="flex items-center mb-6">
          <FontAwesomeIcon icon={faBullseye} className="w-8 h-8 text-amber-600 dark:text-amber-400 mr-3" />
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Trading-Empfehlung: {recommendation.symbolTicker}
          </h1>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Basic Information */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
              Grundinformationen
            </h2>

            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600 dark:text-gray-400">Symbol:</span>
                <span className="font-medium text-gray-900 dark:text-white">{recommendation.symbolTicker}{recommendation.symbol?.name !== '' && ` (${recommendation.symbol?.name})`}</span>
              </div>

              {recommendation.stockExchange?.identifier !== null && recommendation.stockExchange?.identifier !== undefined && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 dark:text-gray-400">Börse:</span>
                  <span className="font-medium text-gray-900 dark:text-white">{recommendation.stockExchange.identifier}</span>
                </div>
              )}

              {(recommendationType === 'SWING' || recommendationType === 'LONGTERM') ? (
                <>
                  {(() => {
                    const trade = recommendation as RecommendationSwingtrade | LongTermRecommendation;
                    const isFinished = trade.state === 'FINISHED_PROFIT' || trade.state === 'FINISHED_LOSS';
                    const currency = recommendation.symbol?.currentData?.currency || 'USD';

                    return (
                      <>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600 dark:text-gray-400">Trade-Richtung:</span>
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                            trade.type === 'LONG'
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                              : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                          }`}>
                            {trade.type}
                          </span>
                        </div>

                        {isFinished ? (
                          <>
                            {/* Bei beendeten Trades: Ø-Einstieg, Ø-Ausstieg, Rendite */}
                            <div className="flex justify-between items-center">
                              <span className="text-gray-600 dark:text-gray-400">Ø-Einstieg:</span>
                              <span className="font-medium text-gray-900 dark:text-white">
                                {recommendationType === 'SWING'
                                  ? formatCurrencyCentValue((trade as RecommendationSwingtrade).averageBuyPrice, currency)
                                  : formatCurrencyCentValue((trade as LongTermRecommendation).averageBuyPrice, currency)
                                }
                              </span>
                            </div>

                            <div className="flex justify-between items-center">
                              <span className="text-gray-600 dark:text-gray-400">Ø-Ausstieg:</span>
                              <span className="font-medium text-gray-900 dark:text-white">
                                {trade.averageExitPrice !== null
                                  ? formatCurrencyCentValue(trade.averageExitPrice, currency)
                                  : '-'
                                }
                              </span>
                            </div>

                            <div className="flex justify-between items-center">
                              <span className="text-gray-600 dark:text-gray-400">Rendite:</span>
                              <span className={`font-medium ${
                                trade.yield >= 0
                                  ? 'text-green-600 dark:text-green-400'
                                  : 'text-red-600 dark:text-red-400'
                              }`}>
                                {trade.yield >= 0 ? '+' : ''}{(trade.yield * 100).toFixed(2)}%
                              </span>
                            </div>
                          </>
                        ) : (
                          <>
                            {/* Bei offenen Trades: Wie bisher */}
                            <div className="flex justify-between items-center">
                              <span className="text-gray-600 dark:text-gray-400">Initialpreis:</span>
                              <span className="font-medium text-gray-900 dark:text-white">
                                {formatCurrencyCentValue(trade.initialPrice, currency)}
                              </span>
                            </div>

                            {/* takeProfit only for SwingTrade */}
                            {recommendationType === 'SWING' && (trade as RecommendationSwingtrade).takeProfit > 0 && (
                              <div className="flex justify-between items-center">
                                <span className="text-gray-600 dark:text-gray-400">Take-Profit Ziel:</span>
                                <span className="font-medium text-green-600 dark:text-green-400">
                                  {formatCurrencyCentValue((trade as RecommendationSwingtrade).takeProfit, currency)}
                                </span>
                              </div>
                            )}

                            <div className="flex justify-between items-center">
                              <span className="text-gray-600 dark:text-gray-400">Aktueller Preis:</span>
                              <span className="font-medium text-gray-900 dark:text-white">
                                {formatCurrencyCentValue(trade.currentPrice, currency)}
                              </span>
                            </div>

                            {/* averagePrice and currentPosition only for SwingTrade */}
                            {recommendationType === 'SWING' && (
                              <>
                                <div className="flex justify-between items-center">
                                  <span className="text-gray-600 dark:text-gray-400">Durchschnittspreis:</span>
                                  <span className="font-medium text-gray-900 dark:text-white">
                                    {formatCurrencyCentValue((trade as RecommendationSwingtrade).averagePrice, currency)}
                                  </span>
                                </div>

                                <div className="flex justify-between items-center">
                                  <span className="text-gray-600 dark:text-gray-400">Aktuelle Position:</span>
                                  <span className="font-medium text-gray-900 dark:text-white">
                                    {formatPercentage((trade as RecommendationSwingtrade).currentPosition)}
                                  </span>
                                </div>
                              </>
                            )}
                          </>
                        )}

                        <div className="flex justify-between items-center">
                          <span className="text-gray-600 dark:text-gray-400">Anzahl Einstiege:</span>
                          <span className="font-medium text-gray-900 dark:text-white">
                            {trade.adjustements.length}
                          </span>
                        </div>
                      </>
                    );
                  })()}
                </>
              ) : (
                <>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 dark:text-gray-400">Ausübungspreis (Strike):</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {formatCurrencyCentValue((recommendation as RecommendationCore | RecommendationEarnings).strikePrice, recommendation.symbol?.currentData?.currency || 'USD')}
                    </span>
                  </div>

                  <div className="flex justify-between items-start">
                    <span className="text-gray-600 dark:text-gray-400">Prämie / Kontrakt:</span>
                    <div className="flex flex-col items-end">
                      <span className="font-medium text-gray-900 dark:text-white">
                        {formatCurrencyCentValue((recommendation as RecommendationCore | RecommendationEarnings).premium, recommendation.symbol?.currentData?.currency || 'USD')}
                      </span>
                      {calculatePremiumPercentage(
                        (recommendation as RecommendationCore | RecommendationEarnings).premium,
                        (recommendation as RecommendationCore | RecommendationEarnings).strikePrice
                      ) !== null && (
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          ({(calculatePremiumPercentage(
                            (recommendation as RecommendationCore | RecommendationEarnings).premium,
                            (recommendation as RecommendationCore | RecommendationEarnings).strikePrice
                          )! * 100).toFixed(2)}%)
                        </span>
                      )}
                    </div>
                  </div>
                </>
              )}

              <div className="flex justify-between items-center">
                <span className="text-gray-600 dark:text-gray-400">Status:</span>
                <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStateBadgeColor(recommendation)}`}>
                  {getStateLabel(recommendation)}
                </span>
              </div>

            </div>
          </div>

          {/* Right column: Zeitinformationen + Kommentar */}
          {(() => {
            const hasComment =
              (recommendationType === 'SWING' || recommendationType === 'LONGTERM') &&
              (recommendation as RecommendationSwingtrade | LongTermRecommendation).notificationComment !== '';

            return (
          <div className="flex flex-col gap-8">
            {/* Time Information */}
            <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6${hasComment ? '' : ' lg:flex-grow'}`}>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
                <FontAwesomeIcon icon={faCalendarAlt} className="w-5 h-5 mr-2" />
                Zeitinformationen
              </h2>

              <div className="space-y-4">
                <div>
                  <span className="text-gray-600 dark:text-gray-400 block mb-1">
                    {(recommendationType === 'SWING' || recommendationType === 'LONGTERM') ? 'Einstiegszeitpunkt:' : 'Startdatum:'}
                  </span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {(recommendationType === 'SWING' || recommendationType === 'LONGTERM')
                      ? formatDate((recommendation as RecommendationSwingtrade | LongTermRecommendation).startTimestamp)
                      : formatRFCDate((recommendation as RecommendationCore | RecommendationEarnings).startDate)
                    }
                  </span>
                </div>

                {(recommendationType === 'SWING' || recommendationType === 'LONGTERM') ? (
                  (recommendation as RecommendationSwingtrade | LongTermRecommendation).endTimestamp && (
                    <div>
                      <span className="text-gray-600 dark:text-gray-400 block mb-1">Ausstiegszeitpunkt:</span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {formatDate((recommendation as RecommendationSwingtrade | LongTermRecommendation).endTimestamp!)}
                      </span>
                    </div>
                  )
                ) : (
                  'endDate' in recommendation && recommendation.endDate && (
                    <div>
                      <span className="text-gray-600 dark:text-gray-400 block mb-1">Enddatum:</span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {formatRFCDate(recommendation.endDate)}
                      </span>
                    </div>
                  )
                )}

                <div>
                  <span className="text-gray-600 dark:text-gray-400 block mb-1">Erstellt:</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {formatDate(recommendation.created)}
                  </span>
                </div>

                {recommendation.updated && (
                  <div>
                    <span className="text-gray-600 dark:text-gray-400 block mb-1">Zuletzt aktualisiert:</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {formatDate(recommendation.updated)}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Kommentar - Swing and Long-Term Trades only */}
            {hasComment && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6 lg:flex-grow">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                  <FontAwesomeIcon icon={faComments} className="w-5 h-5 mr-2" />
                  Kommentar
                </h2>
                <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap break-words">
                  {(recommendation as RecommendationSwingtrade | LongTermRecommendation).notificationComment}
                </p>
              </div>
            )}
          </div>
            );
          })()}

          {/* Symbol Information */}
          {recommendation.symbol && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6 lg:col-span-2">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
                <FontAwesomeIcon icon={faChartLine} className="w-5 h-5 mr-2" />
                Symbol-Informationen
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <span className="text-gray-600 dark:text-gray-400 block mb-1">Aktueller Preis:</span>
                  <span className="font-medium text-gray-900 dark:text-white text-lg">
                    {recommendation.symbol.currentData?.price !== null ?
                      formatCurrency(recommendation.symbol.currentData?.price || 0, recommendation.symbol.currentData?.currency || 'USD') :
                      'Nicht verfügbar'
                    }
                  </span>
                </div>

                {recommendation.symbol.currentData?.high && (
                  <div>
                    <span className="text-gray-600 dark:text-gray-400 block mb-1">Tageshoch:</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {formatCurrency(recommendation.symbol.currentData.high, recommendation.symbol.currentData?.currency || 'USD')}
                    </span>
                  </div>
                )}

                {recommendation.symbol.currentData?.low && (
                  <div>
                    <span className="text-gray-600 dark:text-gray-400 block mb-1">Tagestief:</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {formatCurrency(recommendation.symbol.currentData.low, recommendation.symbol.currentData?.currency || 'USD')}
                    </span>
                  </div>
                )}

                {recommendation.symbol.currentData?.volume && (
                  <div>
                    <span className="text-gray-600 dark:text-gray-400 block mb-1">Volumen:</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {recommendation.symbol.currentData.volume.toLocaleString()}
                    </span>
                  </div>
                )}

                {recommendation.symbol.currentData?.marketCap && (
                  <div>
                    <span className="text-gray-600 dark:text-gray-400 block mb-1">Marktkapitalisierung:</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {formatCurrency(recommendation.symbol.currentData.marketCap, recommendation.symbol.currentData?.currency || 'USD')}
                    </span>
                  </div>
                )}

                {recommendation.symbol.currentData?.pe && (
                  <div>
                    <span className="text-gray-600 dark:text-gray-400 block mb-1">KGV:</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {recommendation.symbol.currentData.pe.toFixed(2)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Historical Price Chart */}
          {recommendation.symbol && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6 lg:col-span-2">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
                <FontAwesomeIcon icon={faChartLine} className="w-5 h-5 mr-2" />
                Kursverlauf
              </h2>

              {graphLoading ? (
                <div className="flex justify-center items-center py-12">
                  <FontAwesomeIcon icon={faSpinner} className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin" />
                </div>
              ) : graphPoints.length > 0 ? (
                <div className="space-y-4">
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    Zeitraum: {
                      (recommendationType === 'SWING' || recommendationType === 'LONGTERM')
                        ? `${formatDate((recommendation as RecommendationSwingtrade | LongTermRecommendation).startTimestamp)} bis ${(recommendation as RecommendationSwingtrade | LongTermRecommendation).endTimestamp ? formatDate((recommendation as RecommendationSwingtrade | LongTermRecommendation).endTimestamp!) : 'heute'}`
                        : `${formatRFCDate((recommendation as RecommendationCore | RecommendationEarnings).startDate)} bis ${formatRFCDate((recommendation as RecommendationCore | RecommendationEarnings).endDate)}`
                    }
                  </div>
                  <SymbolPriceChart
                    points={graphPoints}
                    currency={recommendation.symbol.currentData?.currency || 'USD'}
                    height={400}
                    {...((recommendationType === 'SWING' || recommendationType === 'LONGTERM') && {
                      adjustements: (recommendation as RecommendationSwingtrade | LongTermRecommendation).adjustements
                    })}
                    {...(recommendationType === 'SWING' && {
                      averagePrice: (isFinishedSwingTrade
                        ? (recommendation as RecommendationSwingtrade).averageBuyPrice
                        : (recommendation as RecommendationSwingtrade).averagePrice) / 100,
                      takeProfit: (recommendation as RecommendationSwingtrade).takeProfit > 0
                        ? (recommendation as RecommendationSwingtrade).takeProfit / 100
                        : undefined
                    })}
                    {...((recommendationType !== 'SWING' && recommendationType !== 'LONGTERM') && {
                      strikePrice: (recommendation as RecommendationCore | RecommendationEarnings).strikePrice / 100
                    })}
                  />
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                  <FontAwesomeIcon icon={faChartLine} className="w-16 h-16 mb-4 opacity-50" />
                  <p className="text-lg font-medium">Keine historischen Daten verfügbar</p>
                  <p className="text-sm">
                    Für den Zeitraum {
                      (recommendationType === 'SWING' || recommendationType === 'LONGTERM')
                        ? `${formatDate((recommendation as RecommendationSwingtrade | LongTermRecommendation).startTimestamp)} bis ${(recommendation as RecommendationSwingtrade | LongTermRecommendation).endTimestamp ? formatDate((recommendation as RecommendationSwingtrade | LongTermRecommendation).endTimestamp!) : 'heute'}`
                        : `${formatRFCDate((recommendation as RecommendationCore | RecommendationEarnings).startDate)} bis ${formatRFCDate((recommendation as RecommendationCore | RecommendationEarnings).endDate)}`
                    } sind keine Kursdaten verfügbar.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Adjustments Section - Swing and Long-Term Trades */}
      {(recommendationType === 'SWING' || recommendationType === 'LONGTERM') && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6 mt-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Einstiege
            </h2>
          </div>

          <SwingTradeAdjustementsTable
            swingTrade={recommendation as RecommendationSwingtrade | LongTermRecommendation}
            tradeType={recommendationType === 'SWING' ? 'SWING' : 'LONGTERM'}
            showActions={false}
          />
        </div>
      )}
    </>
  );
}
